import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI per-component imports (never barrel imports)
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// MUI icons
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";

import { importQuestions } from "../vsecuriteSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs, { Localize } from "../../../app/lang/langs";

/** Brand orange used for primary actions throughout the app */
const BRAND_ORANGE = "#FF9015";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/**
 * Template column definitions shown to the user in the instructions section.
 * Each entry describes the column name and its expected content.
 */
const TEMPLATE_COLUMNS = [
    { col: "question_text", desc: "Texte complet de la question" },
    { col: "question_type", desc: 'Type : "single" (une réponse) ou "multiple" (plusieurs réponses)' },
    { col: "option_1 … option_6", desc: "Texte de chaque choix de réponse (laisser vide si non utilisé)" },
    { col: "option_1_correct … option_6_correct", desc: 'TRUE si le choix est correct, FALSE sinon' },
    { col: "explanation", desc: "Explication affichée après réponse (optionnel)" },
];

/**
 * CSV template content — semicolon-separated so Excel opens it directly
 * without any import wizard on French locale systems.
 */
const CSV_TEMPLATE_CONTENT = [
    "question_text;question_type;option_1;option_2;option_3;option_4;option_5;option_6;option_1_correct;option_2_correct;option_3_correct;option_4_correct;option_5_correct;option_6_correct;explanation",
    "Exemple : Quel équipement est obligatoire sur le chantier ?;single;Casque;Lunettes;Gants;Gilet;Chaussures de sécurité;;TRUE;FALSE;FALSE;FALSE;FALSE;;Le casque est l'EPI prioritaire en zone chantier.",
].join("\r\n");

/**
 * QuizImport
 *
 * Admin panel for importing quiz questions from an Excel/CSV file.
 * Displays:
 *   1. Instructions + downloadable template
 *   2. File picker with drag-and-drop zone
 *   3. Import result feedback (success count or per-row error list)
 *
 * Props:
 *   quizId          — numeric ID of the target quiz
 *   onImportSuccess — callback invoked after a successful import
 *   onCancel        — callback invoked when the user cancels
 */
const QuizImport = ({ quizId, onImportSuccess, onCancel }) => {
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const mobile = isMobileRedux === 2 || isMobileScreen;

    /** Currently selected file (File object) or null */
    const [selectedFile, setSelectedFile] = useState(null);

    /** Whether an import request is in flight */
    const [importing, setImporting] = useState(false);

    /**
     * Import result state:
     *   null     — no import done yet
     *   { success: true, count: N }
     *   { success: false, errors: [{ row, message }] }
     */
    const [importResult, setImportResult] = useState(null);

    /** Whether the file drop zone is currently being dragged over */
    const [isDragOver, setIsDragOver] = useState(false);

    /** Hidden file input ref */
    const fileInputRef = useRef(null);

    // ── Template download ────────────────────────────────────────────────────

    /**
     * Generates and triggers download of a CSV template file.
     * Uses a Blob URL so no server round-trip is needed.
     */
    const handleDownloadTemplate = () => {
        const blob = new Blob([CSV_TEMPLATE_CONTENT], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "questions-template.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ── File selection ───────────────────────────────────────────────────────

    /**
     * Extracts the first valid .xlsx/.xls file from a FileList and stores it.
     * Resets any previous import result so the UI is clean.
     */
    const applyFile = (file) => {
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!name.endsWith(".xlsx") && !name.endsWith(".xls") && !name.endsWith(".csv")) {
            showErrorToast(Localize("VSEC_TOAST_INVALID_FILE", lang));
            return;
        }
        setSelectedFile(file);
        setImportResult(null);
    };

    /** Called when the user picks a file via the hidden input */
    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        applyFile(file);
        // Reset input value so the same file can be re-selected after clearing
        event.target.value = "";
    };

    /** Called when the user drops a file onto the drop zone */
    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragOver(false);
        const file = event.dataTransfer.files?.[0];
        applyFile(file);
    };

    /** Clears the currently selected file and any previous result */
    const handleClearFile = () => {
        setSelectedFile(null);
        setImportResult(null);
    };

    // ── Import submission ────────────────────────────────────────────────────

    /**
     * Parses the selected CSV file client-side and dispatches the importQuestions
     * thunk with the parsed questions array.
     */
    const handleImport = async () => {
        if (!selectedFile || !quizId) return;
        setImporting(true);

        try {
            const text = await fileToText(selectedFile);
            const { questions, errors: parseErrors } = parseCSVToQuestions(text);

            if (questions.length === 0) {
                const errs = parseErrors.length
                    ? parseErrors
                    : [{ row: "—", message: Localize("VSEC_IMPORT_EMPTY_FILE", lang) }];
                setImportResult({ success: false, errors: errs });
                showErrorToast(Localize("VSEC_TOAST_IMPORT_ERROR", lang));
                setImporting(false);
                return;
            }

            const result = await dispatch(
                importQuestions({ id_quiz: quizId, questions })
            ).unwrap();

            if (result?.responseData) {
                const data = result.responseData;

                if (data.errors && data.errors.length > 0) {
                    // Partial or full failure — show per-row error list
                    setImportResult({ success: false, errors: data.errors });
                    showErrorToast(Localize("VSEC_TOAST_IMPORT_PARTIAL_ERROR_WITH_COUNT", lang).replace('%n', data.errors.length));
                } else {
                    // Full success
                    const count = data.imported ?? data.count ?? 0;
                    setImportResult({ success: true, count });
                    showSuccessToast(`${count} ${Localize("VSEC_IMPORT_SUCCESS_COUNT", lang)}`);
                    onImportSuccess?.();
                }
            } else {
                showErrorToast(Localize("VSEC_TOAST_UNEXPECTED_RESPONSE", lang));
                setImportResult({ success: false, errors: [{ row: "—", message: Localize("VSEC_IMPORT_SERVER_ERROR_MSG", lang) }] });
            }
        } catch (error) {
            showErrorToast(Localize("VSEC_TOAST_IMPORT_ERROR", lang));
            setImportResult({
                success: false,
                errors: [{ row: "—", message: error?.message ?? Localize("VSEC_IMPORT_UNKNOWN_ERROR_MSG", lang) }],
            });
        } finally {
            setImporting(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: mobile ? 2 : 3,
                p: mobile ? 2 : 3,
                background: mobile ? "#fff" : "rgba(255,255,255,0.8)",
                backdropFilter: mobile ? "none" : "blur(14px)",
                WebkitBackdropFilter: mobile ? "none" : "blur(14px)",
                borderRadius: mobile ? 0 : "20px",
                border: mobile ? "none" : "1px solid rgba(0,40,128,0.06)",
                boxShadow: mobile ? "none" : "0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.05)",
                maxWidth: mobile ? "100%" : 720,
                width: "100%",
                mx: mobile ? 0 : "auto",
                "@keyframes impFadeIn": { from: { opacity: 0, transform: "translateY(12px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                animation: mobile ? "none" : `impFadeIn 400ms ${EASE} both`,
            }}
        >
            {/* ── Section 1 : Instructions ─────────────────────────────────── */}
            <Box>
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#1a1a2e", mb: 0.5, fontSize: "1rem" }}
                >
                    <Langs str="VSEC_IMPORT_TITLE" />
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "0.875rem", mb: 2 }}>
                    <Langs str="VSEC_IMPORT_INSTRUCTIONS" />
                </Typography>

                {/* Template download button */}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadRoundedIcon />}
                    onClick={handleDownloadTemplate}
                    fullWidth={mobile}
                    sx={{
                        borderColor: BRAND_ORANGE,
                        color: BRAND_ORANGE,
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "12px",
                        mb: 2,
                        minHeight: 44,
                        transition: `all 250ms ${EASE}`,
                        "&:hover": {
                            borderColor: "#e07d0d",
                            backgroundColor: "rgba(255,144,21,0.05)",
                            transform: "scale(1.02)",
                        },
                        "&:active": { transform: "scale(0.98)" },
                    }}
                >
                    <Langs str="VSEC_IMPORT_DOWNLOAD_TEMPLATE" />
                </Button>

                {/* Column descriptions */}
                <Box
                    sx={{
                        background: "rgba(247,248,252,0.8)",
                        border: "1px solid rgba(0,40,128,0.06)",
                        borderRadius: "14px",
                        p: mobile ? 1.5 : 2,
                        mb: 1.5,
                        overflowX: mobile ? "auto" : undefined,
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    <Typography
                        sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#333", mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                        {Localize("VSEC_IMPORT_TEMPLATE_COLS_TITLE", lang)}
                    </Typography>

                    {mobile ? (
                        /* Mobile: stacked layout */
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {TEMPLATE_COLUMNS.map((item) => (
                                <Box key={item.col}>
                                    <Typography
                                        component="code"
                                        sx={{
                                            fontSize: "0.75rem",
                                            background: "#fff3e0",
                                            color: BRAND_ORANGE,
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: 1,
                                            fontFamily: "monospace",
                                            fontWeight: 600,
                                            display: "inline-block",
                                            mb: 0.25,
                                        }}
                                    >
                                        {item.col}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8rem", color: "#555" }}>
                                        {item.desc}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        /* Desktop: table layout */
                        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                            <tbody>
                                {TEMPLATE_COLUMNS.map((item) => (
                                    <tr key={item.col}>
                                        <td style={{ padding: "4px 12px 4px 0", verticalAlign: "top", whiteSpace: "nowrap" }}>
                                            <Typography
                                                component="code"
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    background: "#fff3e0",
                                                    color: BRAND_ORANGE,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 1,
                                                    fontFamily: "monospace",
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {item.col}
                                            </Typography>
                                        </td>
                                        <td style={{ padding: "4px 0" }}>
                                            <Typography sx={{ fontSize: "0.8rem", color: "#555" }}>
                                                {item.desc}
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Box>
                    )}
                </Box>

                {/* Example row note */}
                <Alert
                    severity="info"
                    sx={{ fontSize: "0.8rem", borderRadius: 2, "& .MuiAlert-message": { width: "100%" } }}
                >
                    <Langs str="VSEC_IMPORT_EXAMPLE_NOTE" />
                </Alert>
            </Box>

            <Divider />

            {/* ── Section 2 : Upload ───────────────────────────────────────── */}
            <Box>
                <Typography
                    sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1a1a2e", mb: 1.5 }}
                >
                    <Langs str="VSEC_IMPORT_SELECT_FILE" />
                </Typography>

                {/* Drop zone — visible only when no file is selected */}
                {!selectedFile && (
                    <Box
                        onDrop={handleDrop}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: `2px dashed ${isDragOver ? BRAND_ORANGE : "rgba(0,40,128,0.12)"}`,
                            borderRadius: "16px",
                            p: mobile ? 3 : 4,
                            textAlign: "center",
                            cursor: "pointer",
                            background: isDragOver ? "rgba(255,144,21,0.06)" : "rgba(0,40,128,0.02)",
                            transition: `all 300ms ${EASE}`,
                            "&:hover": {
                                borderColor: BRAND_ORANGE,
                                background: "rgba(255,144,21,0.05)",
                                transform: "scale(1.005)",
                            },
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={Localize("VSEC_IMPORT_ZONE_ARIA", lang)}
                        onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                    >
                        <CloudUploadRoundedIcon sx={{ fontSize: mobile ? 36 : 40, color: isDragOver ? BRAND_ORANGE : "#bdbdbd", mb: 1 }} />
                        <Typography sx={{ color: "#555", fontSize: "0.875rem" }}>
                            {Localize("VSEC_IMPORT_DROP_ZONE", lang)}{" "}
                            <span style={{ color: BRAND_ORANGE, fontWeight: 600 }}>{Localize("VSEC_IMPORT_BROWSE", lang)}</span>
                        </Typography>
                        <Typography sx={{ color: "#9e9e9e", fontSize: "0.75rem", mt: 0.5 }}>
                            <Langs str="VSEC_IMPORT_FORMATS" />
                        </Typography>

                        {/* Hidden native file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                    </Box>
                )}

                {/* Selected file display */}
                {selectedFile && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: "14px",
                            border: "1px solid rgba(0,40,128,0.08)",
                            background: "rgba(247,248,252,0.8)",
                            transition: `all 250ms ${EASE}`,
                            flexWrap: mobile ? "wrap" : "nowrap",
                        }}
                    >
                        <InsertDriveFileRoundedIcon sx={{ color: BRAND_ORANGE, fontSize: 28, flexShrink: 0 }} />
                        <Typography sx={{ flex: 1, fontWeight: 600, fontSize: "0.875rem", color: "#333", wordBreak: "break-all" }}>
                            {selectedFile.name}
                        </Typography>
                        <Typography sx={{ color: "#9e9e9e", fontSize: "0.75rem", flexShrink: 0 }}>
                            {(selectedFile.size / 1024).toFixed(1)} Ko
                        </Typography>
                        {/* Clear file button */}
                        <Button
                            size="small"
                            startIcon={<CancelRoundedIcon />}
                            onClick={handleClearFile}
                            disabled={importing}
                            sx={{
                                color: "#757575",
                                textTransform: "none",
                                fontSize: "0.78rem",
                                minHeight: 44,
                                "&:hover": { color: "#c62828" },
                            }}
                        >
                            <Langs str="VSEC_BTN_REMOVE_FILE" />
                        </Button>
                    </Box>
                )}

                {/* Action buttons row */}
                <Box sx={{
                    display: "flex",
                    flexDirection: mobile ? "column-reverse" : "row",
                    gap: 1.5,
                    mt: 2,
                    justifyContent: "flex-end",
                }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={importing}
                        fullWidth={mobile}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "12px",
                            borderColor: "rgba(0,40,128,0.15)",
                            color: "#555",
                            minHeight: 48,
                            transition: `all 250ms ${EASE}`,
                            "&:hover": { borderColor: "rgba(0,40,128,0.3)", background: "rgba(0,40,128,0.03)", transform: "scale(1.02)" },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={!selectedFile || importing}
                        fullWidth={mobile}
                        startIcon={
                            importing ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : (
                                <CloudUploadRoundedIcon />
                            )
                        }
                        sx={{
                            backgroundColor: BRAND_ORANGE,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "12px",
                            boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                            minHeight: 48,
                            minWidth: 110,
                            transition: `all 250ms ${EASE}`,
                            "&:hover": { backgroundColor: "#e07d0d", boxShadow: "0 6px 20px rgba(255,144,21,0.35)", transform: "translateY(-1px) scale(1.02)" },
                            "&:active": { transform: "scale(0.98)" },
                            "&:disabled": { backgroundColor: "rgba(255,144,21,0.4)" },
                        }}
                    >
                        {importing ? <Langs str="VSEC_BTN_IMPORTING" /> : <Langs str="VSEC_BTN_IMPORT" />}
                    </Button>
                </Box>
            </Box>

            {/* ── Section 3 : Import result ─────────────────────────────────── */}
            {importResult && (
                <>
                    <Divider />
                    <ImportResult result={importResult} lang={lang} />
                </>
            )}
        </Box>
    );
};

// ── Internal sub-components ──────────────────────────────────────────────────

/**
 * ImportResult
 *
 * Renders either the success message or the error list after an import attempt.
 *
 * Props:
 *   result — { success: true, count } | { success: false, errors: [{row, message}] }
 */
const ImportResult = ({ result, lang }) => {
    if (result.success) {
        return (
            <Alert
                severity="success"
                icon={<CheckCircleRoundedIcon fontSize="inherit" />}
                sx={{ borderRadius: 2, fontWeight: 600 }}
            >
                {result.count} {Localize("VSEC_IMPORT_SUCCESS_COUNT", lang)}
            </Alert>
        );
    }

    return (
        <Box>
            <Alert
                severity="error"
                icon={<ErrorRoundedIcon fontSize="inherit" />}
                sx={{ borderRadius: 2, mb: 1.5 }}
            >
                {Localize("VSEC_IMPORT_ERROR_MSG", lang)}
            </Alert>
            <Box
                sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    border: "1px solid rgba(198,40,40,0.2)",
                    borderRadius: 2,
                    background: "#fff",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <List dense disablePadding>
                    {result.errors.map((err, index) => (
                        <ListItem
                            key={index}
                            divider={index < result.errors.length - 1}
                            sx={{ py: 0.75, px: 2 }}
                        >
                            <ListItemText
                                primary={
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        <Typography
                                            component="span"
                                            sx={{
                                                fontWeight: 700,
                                                color: "#c62828",
                                                mr: 1,
                                                fontSize: "0.8rem",
                                            }}
                                        >
                                            {Localize("VSEC_IMPORT_ROW_PREFIX", lang)} {err.row} :
                                        </Typography>
                                        {err.message}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

// ── Utility ──────────────────────────────────────────────────────────────────

/**
 * Reads a File as UTF-8 text.
 * @param {File} file
 * @returns {Promise<string>}
 */
const fileToText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsText(file, "UTF-8");
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

/**
 * Parses a semicolon-delimited CSV string (as produced by the template) into
 * the questions array format expected by the backend importQuestions endpoint.
 *
 * Expected header row:
 *   question_text;question_type;option_1;...;option_6;
 *   option_1_correct;...;option_6_correct;explanation
 *
 * @param {string} csvText
 * @returns {{ questions: Array, errors: Array<{row: number, message: string}> }}
 */
const parseCSVToQuestions = (csvText) => {
    // Strip UTF-8 BOM if present (Excel adds it)
    const cleaned = csvText.replace(/^\uFEFF/, "").trim();
    const lines = cleaned.split(/\r?\n/).filter((l) => l.trim());

    if (lines.length < 2) return { questions: [], errors: [] };

    const headers = lines[0].split(";").map((h) => h.trim().toLowerCase());
    const questions = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(";");
        const row = {};
        headers.forEach((h, idx) => {
            row[h] = (values[idx] ?? "").trim();
        });

        const question_text = row["question_text"] || "";
        if (!question_text) {
            errors.push({ row: i + 1, message: "Texte de question manquant" });
            continue;
        }

        const options = [];
        for (let j = 1; j <= 6; j++) {
            const text = row[`option_${j}`] || "";
            if (!text) continue;
            const correctRaw = (row[`option_${j}_correct`] || "").toUpperCase();
            options.push({
                option_text: text,
                is_correct: correctRaw === "TRUE" || correctRaw === "1",
            });
        }

        if (options.length < 2) {
            errors.push({ row: i + 1, message: "Moins de 2 options fournies" });
            continue;
        }
        if (!options.some((o) => o.is_correct)) {
            errors.push({ row: i + 1, message: "Aucune option correcte" });
            continue;
        }

        const qType = (row["question_type"] || "single").toLowerCase();
        questions.push({
            question_text,
            type: qType === "multiple" ? "multiple" : "single",
            explanation: row["explanation"] || null,
            options,
        });
    }

    return { questions, errors };
};

export default QuizImport;
